import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AiOutlineSearch } from "react-icons/ai";
import {
  GridComponent,
  Page,
  Inject,
  ColumnDirective,
  ColumnsDirective,
  Filter,
} from "@syncfusion/ej2-react-grids";

// Components
import Spinner from "../../Components/Spinner";
import EmptyData from "../../../components/EmptyData";
import SearchInputField from "../../Components/Forms/SearchInputField";
import SearchInputDocument from "../../Components/Forms/SearchInputDocument";
import { ToastComponent } from "../../Components/ToastComponent";

import "./CustomerSearch.css";

// Abas do cliente selecionado — cada uma é uma pasta própria dentro de
// Pages/Customer, com seu próprio componente.
import PersonalData from "../personalData/PersonalData";
import AdministrativeData from "../administrativeData/AdministrativeData";
import Address from "../address/Address";
import ContactsList from "../contactsList/ContactsList";
import Notes from "../notes/Notes";
import Settings from "../settings/Settings";
import CommercialCondition from "../commercialCondition/CommercialCondition";
import Assets from "../assets/Assets";

// Services
import { clientService } from "../../../Services/client.service";
import { paginationService } from "../../../Services/pagination.service";

const formatDocument = (doc) => {
  const digits = (doc ?? "").toString().replace(/\D/g, "");
  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  }
  if (digits.length === 14) {
    return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  }
  return doc ?? "";
};


const EMPTY_ADDRESS = {
  cep: "",
  address: "",
  number: "",
  neighborhood: "",
  complement: "",
  uf: "",
  city: "",
};

// Abas de categorias de dados do cliente. Cada aba nova = uma pasta nova em
// Pages/Customer com seu componente, importado e listado aqui.
const PERSONAL_DATA_TAB_KEY = "personalData";

const TABS = [
  { key: PERSONAL_DATA_TAB_KEY, labelKey: "CUSTOMER.TAB_PERSONAL_DATA", Component: PersonalData },
  { key: "administrativeData", labelKey: "CUSTOMER.TAB_ADMINISTRATIVE_DATA", Component: AdministrativeData },
  { key: "address", labelKey: "CUSTOMER.TAB_ADDRESS", Component: Address },
  { key: "contactsList", labelKey: "CUSTOMER.TAB_CONTACTS", Component: ContactsList },
  { key: "notes", labelKey: "CUSTOMER.TAB_NOTES", Component: Notes },
  { key: "settings", labelKey: "CUSTOMER.TAB_SETTINGS", Component: Settings },
  { key: "commercialCondition", labelKey: "CUSTOMER.TAB_COMMERCIAL_CONDITION", Component: CommercialCondition },
  { key: "assets", labelKey: "CUSTOMER.TAB_ASSETS", Component: Assets },
];

// Tela inicial de busca de clientes por nome ou CPF/CNPJ, com resultado em
// grid Syncfusion (padrão de altura responsiva usado em FretamentoClients.jsx
// e VehiclesNickame.jsx). A tabela de resultados não pertence a nenhuma aba:
// ela só aparece logo após uma busca (showResults) e some assim que o
// usuário seleciona qualquer aba ou clica em um cliente da lista — nesse
// caso a navegação vai direto para a aba de Dados Pessoais.
//
// Fluxo "Novo Cliente" (issue #499): ao clicar, abre direto a aba Dados
// Pessoais sem nenhum clientId associado (selectedClientId = null) e sem
// disparar busca nenhuma. Enquanto selectedClientId for null, todas as
// abas exceto Dados Pessoais ficam bloqueadas (disabled). O desbloqueio
// deveria acontecer via handleClientSaved, chamado pela aba Dados Pessoais
// após um POST de criação bem-sucedido — TODO: ligar isso de verdade
// quando o endpoint de criar cliente existir (ainda não documentado no
// swagger do Customer API).
export default function CustomerSearch() {
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState(TABS[0].key);
  const [nameValue, setNameValue] = useState("");
  const [documentValue, setDocumentValue] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [clients, setClients] = useState([]);
  const [toastParams, setToastParams] = useState("");

  // Cliente atualmente "aberto" nas abas. null = nenhum cliente carregado
  // (estado inicial ou fluxo de Novo Cliente antes de salvar).
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [isNewCustomer, setIsNewCustomer] = useState(false);
  const [selectedClientLabel, setSelectedClientLabel] = useState(null);

  const [mainAddress, setMainAddress] = useState(EMPTY_ADDRESS);
  const [billingAddress, setBillingAddress] = useState(EMPTY_ADDRESS);
  const [sameAsMain, setSameAsMain] = useState(false);
  // Guarda pra qual clientId o endereço já foi carregado do backend — evita
  // que Address.jsx recarregue (e descarte edição não salva) toda vez que o
  // usuário troca de aba e volta pra Endereço sem clicar em Salvar.
  const [addressLoadedClientId, setAddressLoadedClientId] = useState(null);

  // Altura e paginação dinâmicos do grid
  const [pageSize, setPageSize] = useState(15);
  const gridAnchorRef = useRef(null);

  const ROW_HEIGHT = 36;
  // gridAnchorRef já mede o "top" real do grid (getBoundingClientRect), então
  // esse budget cobre só o que fica ABAIXO dele — cabeçalho + pager do próprio
  // grid Syncfusion — e não deve tentar cobrir de novo o formulário de busca
  // e o cabeçalho da tela, que já estão embutidos no "top" medido.
  const HEADER_PAGER_BUDGET = 70;
  const BOTTOM_MARGIN = 134;
  const MIN_PAGE = 5;
  const MAX_PAGE = 100;

  const showToast = (title, body, color) => {
    setToastParams(JSON.stringify({ show: true, title, body, color }));
  };

  const calculateGridMetrics = () => {
    paginationService.calculateGridMetrics(
      gridAnchorRef,
      BOTTOM_MARGIN,
      HEADER_PAGER_BUDGET,
      ROW_HEIGHT,
      MIN_PAGE,
      MAX_PAGE,
      clients,
      window.innerHeight,
      setPageSize
    );
  };

  // Recalcula depois que a grid de resultados realmente pintou na tela (só
  // então gridAnchorRef.current tem posição válida) — o setTimeout chamado
  // direto de dentro de handleSearch era uma corrida contra o mount da
  // Syncfusion Grid e podia medir errado, deixando linhas demais e
  // estourando a altura da página (scroll fora da grid). Também reage a
  // resize da janela, igual VehiclesNickame.jsx.
  useEffect(() => {
    if (!showResults) return undefined;

    calculateGridMetrics();

    let resizeTimeout = null;
    const onResize = () => {
      if (resizeTimeout) window.clearTimeout(resizeTimeout);
      resizeTimeout = window.setTimeout(calculateGridMetrics, 50);
    };

    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      if (resizeTimeout) window.clearTimeout(resizeTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showResults, clients]);

  const handleSearch = async (e) => {
    e?.preventDefault?.();

    const doc = documentValue.replace(/\D/g, "");
    const payload = doc ? { Doc: doc } : { ClientName: nameValue.trim() };

    if (!payload.ClientName && !payload.Doc) {
      showToast(t("COMMON.ATTENTION"), t("CUSTOMER.FILL_SEARCH_HINT"), "Warning");
      return;
    }


    setIsNewCustomer(false);
    setSelectedClientId(null);
    setSelectedClientLabel(null);
    setIsSearching(true);

    try {
      const response = await clientService.search(payload);
      const items = Array.isArray(response) ? response : response?.items || (response ? [response] : []);
      setClients(items);
    } catch (error) {
      showToast(t("COMMON.ERROR"), t("CUSTOMER.SEARCH_ERROR"), "Danger");
      setClients([]);
    } finally {
      setHasSearched(true);
      setShowResults(true);
      setIsSearching(false);
    }
  };

  const handleNewCustomer = () => {
    setIsNewCustomer(true);
    setSelectedClientId(null);
    setSelectedClientLabel(null);
    setShowResults(false);
    setActiveTab(PERSONAL_DATA_TAB_KEY);
  };

  const handleSelectTab = (tabKey) => {
    setActiveTab(tabKey);
    setShowResults(false);
  };

  const handleRowSelected = (args) => {
    const clientId = args?.data?.clientId;
    if (clientId) {
      setIsNewCustomer(false);
      setSelectedClientId(clientId);
      setSelectedClientLabel({ id: clientId, name: args?.data?.clientName ?? "" });
      handleSelectTab(PERSONAL_DATA_TAB_KEY);
    }
  };


  const handleClientSaved = (newClientId, clientName) => {
    setSelectedClientId(newClientId);
    setIsNewCustomer(false);
    setSelectedClientLabel({ id: newClientId, name: clientName ?? "" });
  };

  const isTabLocked = (tabKey) =>
    tabKey !== PERSONAL_DATA_TAB_KEY && !selectedClientId;

  const ActiveTabComponent = TABS.find((tab) => tab.key === activeTab)?.Component;
  const showTabsAndContent = hasSearched || isNewCustomer;

  return (
    <div className="card">
      <ToastComponent
        params={toastParams}
        closeToast={() => setToastParams(JSON.stringify({ show: false }))}
      />

      <div className="bg-primary p-3 rounded-top">
        <strong style={{ color: "var(--text-primary)" }}>{t("CUSTOMER.SEARCH_INSTRUCTION")}</strong>
      </div>

      <div className="p-3">
        <form onSubmit={handleSearch}>
          <div className="row g-2 align-items-end">
            <div className="col-md-4">
              <SearchInputField
                name="clientName"
                label={t("COMMON.NAME")}
                type="text"
                tabIndex="1"
                onChange={(e) => setNameValue(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <SearchInputDocument
                name="document"
                label={t("COMMON.CPF_CNPJ")}
                value={documentValue}
                onChange={(e) => setDocumentValue(e.target.value)}
              />
            </div>
            <div className="col-md-auto">
              <button type="submit" className="btn btn-primary" disabled={isSearching}>
                <AiOutlineSearch className="icon-in-button" />
                {t("COMMON.SEARCH")}
              </button>
            </div>
            <div className="col-md-auto ms-md-auto">
              <button type="button" className="btn btn-primary" onClick={handleNewCustomer}>
                {t("CUSTOMER.NEW_CUSTOMER")}
              </button>
            </div>
          </div>
        </form>
      </div>

      {selectedClientLabel && (
        <div className="px-3 pb-3">
          <div className="alert alert-secondary py-2 mb-0">
            {t("CUSTOMER.SELECTED_CLIENT_LABEL", {
              id: selectedClientLabel.id,
              name: selectedClientLabel.name || t("CUSTOMER.NEW_CUSTOMER"),
            })}
          </div>
        </div>
      )}

      {isSearching ? (
        <div className="p-3">
          <Spinner />
        </div>
      ) : !showTabsAndContent ? (
        <div className="p-3">
          <p className="text-muted mb-0">{t("CUSTOMER.SEARCH_HINT")}</p>
        </div>
      ) : (
        <>
          {/* Sem cliente selecionado ainda (resultado da busca em exibição),
              nenhuma aba aparece — só depois de clicar num cliente da lista
              (ou de "Novo Cliente") elas ficam visíveis. */}
          {!showResults && (
            <div className="px-3 mt-3 mb-3 customer-tabs" role="tablist">
              {TABS.map((tab) => {
                const locked = isTabLocked(tab.key);
                return (
                  <button
                    key={tab.key}
                    type="button"
                    className={activeTab === tab.key ? "customer-tab active" : "customer-tab"}
                    onClick={() => handleSelectTab(tab.key)}
                    disabled={locked}
                    title={locked ? t("CUSTOMER.TAB_LOCKED_TOOLTIP") : undefined}
                    role="tab"
                    aria-selected={activeTab === tab.key}
                    aria-disabled={locked}
                  >
                    {t(tab.labelKey)}
                  </button>
                );
              })}
            </div>
          )}

          {isNewCustomer && (
            <div className="px-3">
              <div className="alert alert-info">
                {t("CUSTOMER.NEW_CUSTOMER_BLOCKED_HINT")}
              </div>
            </div>
          )}

          {showResults ? (
            <>
              <div ref={gridAnchorRef} />

              <div className="p-3 pt-0">
                {clients.length === 0 ? (
                  <EmptyData />
                ) : (
                  <GridComponent
                    style={{ marginTop: 16 }}
                    dataSource={clients.map((client) => ({
                      ...client,
                      formattedDoc: formatDocument(client.doc),
                    }))}
                    allowPaging
                    allowFiltering
                    filterSettings={{ type: "Menu" }}
                    pageSettings={{ pageSize }}
                    rowHeight={ROW_HEIGHT}
                    height="auto"
                    locale="pt-BR"
                    rowSelected={handleRowSelected}
                    rowDataBound={(args) => {
                      args.row.style.cursor = "pointer";
                    }}
                    emptyRecordTemplate={() => <EmptyData />}
                    actionComplete={calculateGridMetrics}
                  >
                    <ColumnsDirective>
                      <ColumnDirective field="clientName" headerText={t("CUSTOMER.COLUMN_NAME")} width="200" />
                      <ColumnDirective field="clientTradeName" headerText={t("CUSTOMER.COLUMN_TRADE_NAME")} width="220" />
                      <ColumnDirective field="formattedDoc" headerText={t("COMMON.CPF_CNPJ")} width="150" />
                      <ColumnDirective field="contractQuantity" headerText={t("CUSTOMER.COLUMN_ACTIVE_CONTRACTS")} width="150" textAlign="Center" />
                      <ColumnDirective field="representativeName" headerText={t("CUSTOMER.COLUMN_REPRESENTATIVE")} width="180" />
                    </ColumnsDirective>
                    <Inject services={[Page, Filter]} />
                  </GridComponent>
                )}
              </div>
            </>
          ) : (
            ActiveTabComponent && (
              <ActiveTabComponent
                clientId={selectedClientId}
                isNewCustomer={isNewCustomer}
                onClientSaved={handleClientSaved}
                mainAddress={mainAddress}
                billingAddress={billingAddress}
                sameAsMain={sameAsMain}
                onMainAddressChange={setMainAddress}
                onBillingAddressChange={setBillingAddress}
                onSameAsMainChange={setSameAsMain}
                addressLoadedClientId={addressLoadedClientId}
                onAddressLoaded={setAddressLoadedClientId}
              />
            )
          )}
        </>
      )}
    </div>
  );
}
